import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  IconButton,
  TextField,
  Button,
  Divider,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Badge,
  InputAdornment,
  Paper,
  CircularProgress,
} from '@mui/material';
import {
  ThumbUp as LikeIcon,
  ThumbUpOutlined as LikeOutlinedIcon,
  ChatBubbleOutline as CommentIcon,
  Share as ShareIcon,
  MoreVert as MoreVertIcon,
  Send as SendIcon,
  AttachFile as AttachIcon,
  Image as ImageIcon,
  EmojiEmotions as EmojiIcon,
  Add as AddIcon,
  Group as GroupIcon,
  NotificationsOff as MuteIcon,
  Delete as DeleteIcon,
  Flag as FlagIcon,
  Bookmark as BookmarkIcon,
  BookmarkBorder as BookmarkOutlineIcon,
  Search as SearchIcon,
  Public as PublicIcon,
  Lock as PrivateIcon,
} from '@mui/icons-material';
import { useAuthStore } from '../../stores/authStore';
import api from '../../utils/api';

interface StreamPost {
  id: string;
  author: {
    name: string;
    email: string;
    avatar?: string;
  };
  content: string;
  timestamp: Date;
  likes: string[];
  comments: StreamComment[];
  attachments?: { name: string; type: string; url: string }[];
  isBookmarked: boolean;
  streamId: string;
  streamName: string;
}

interface StreamComment {
  id: string;
  author: {
    name: string;
    email: string;
    avatar?: string;
  };
  content: string;
  timestamp: Date;
  likes: string[];
}

interface Stream {
  id: string;
  name: string;
  description: string;
  type: 'public' | 'private' | 'team';
  memberCount: number;
  unreadCount: number;
  isMuted: boolean;
  isOwner: boolean;
}

// Demo data removed — using real API

const StreamsPage: React.FC = () => {
  const { user } = useAuthStore();
  const [selectedTab, setSelectedTab] = useState(0);
  const [streams, setStreams] = useState<Stream[]>([]);
  const [posts, setPosts] = useState<StreamPost[]>([]);
  const [selectedStream, setSelectedStream] = useState<Stream | null>(null);
  const [newPostContent, setNewPostContent] = useState('');
  const [commentTexts, setCommentTexts] = useState<Record<string, string>>({});
  const [showCreateStream, setShowCreateStream] = useState(false);
  const [newStreamName, setNewStreamName] = useState('');
  const [newStreamDesc, setNewStreamDesc] = useState('');
  const [newStreamType, setNewStreamType] = useState<'public' | 'private' | 'team'>('team');
  const [moreMenuAnchor, setMoreMenuAnchor] = useState<{ anchor: HTMLElement; postId: string } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  const currentUserId = user?.email || 'currentUser';

  const fetchStreams = useCallback(async () => {
    try {
      const res = await api.get('/streams');
      setStreams(res.data);
    } catch {
      // Streams may not exist yet — that's okay
    }
  }, []);

  const fetchPosts = useCallback(async (streamId?: string) => {
    try {
      const params: any = {};
      if (streamId) params.streamId = streamId;
      const res = await api.get('/streams/posts', { params });
      setPosts(res.data.posts || []);
    } catch {
      setPosts([]);
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchStreams(), fetchPosts()]);
      setLoading(false);
    };
    load();
  }, [fetchStreams, fetchPosts]);

  useEffect(() => {
    fetchPosts(selectedStream?.id);
  }, [selectedStream, fetchPosts]);

  const handleCreatePost = async () => {
    if (!newPostContent.trim()) return;
    const streamId = selectedStream?.id;
    if (!streamId) return;

    try {
      const res = await api.post('/streams/posts', {
        streamId,
        content: newPostContent,
      });
      setPosts([res.data, ...posts]);
      setNewPostContent('');
    } catch {
      // handle error silently
    }
  };

  const handleLikePost = async (postId: string) => {
    try {
      const res = await api.put(`/streams/posts/${postId}/like`);
      setPosts(posts.map(post =>
        post.id === postId ? { ...post, likes: res.data.likes } : post
      ));
    } catch {
      // optimistic toggle fallback
      setPosts(posts.map(post => {
        if (post.id === postId) {
          const isLiked = post.likes.includes(currentUserId);
          return {
            ...post,
            likes: isLiked
              ? post.likes.filter(id => id !== currentUserId)
              : [...post.likes, currentUserId],
          };
        }
        return post;
      }));
    }
  };

  const handleBookmarkPost = async (postId: string) => {
    try {
      const res = await api.put(`/streams/posts/${postId}/bookmark`);
      setPosts(posts.map(post =>
        post.id === postId ? { ...post, isBookmarked: res.data.isBookmarked } : post
      ));
    } catch {
      setPosts(posts.map(post =>
        post.id === postId ? { ...post, isBookmarked: !post.isBookmarked } : post
      ));
    }
  };

  const handleAddComment = async (postId: string) => {
    const commentText = commentTexts[postId];
    if (!commentText?.trim()) return;

    try {
      const res = await api.post(`/streams/posts/${postId}/comments`, {
        content: commentText,
      });
      setPosts(posts.map(post => {
        if (post.id === postId) {
          return { ...post, comments: [...post.comments, res.data] };
        }
        return post;
      }));
      setCommentTexts({ ...commentTexts, [postId]: '' });
    } catch {
      // handle error silently
    }
  };

  const handleDeletePost = async (postId: string) => {
    setMoreMenuAnchor(null);
    try {
      await api.delete(`/streams/posts/${postId}`);
      setPosts(posts.filter(p => p.id !== postId));
    } catch {
      // handle error silently
    }
  };

  const handleCreateStream = async () => {
    if (!newStreamName.trim()) return;
    try {
      const res = await api.post('/streams', {
        name: newStreamName,
        description: newStreamDesc,
        type: newStreamType,
      });
      setStreams([res.data, ...streams]);
      setShowCreateStream(false);
      setNewStreamName('');
      setNewStreamDesc('');
    } catch {
      // handle error silently
    }
  };

  const formatTimeAgo = (date: Date) => {
    const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
    if (seconds < 60) return 'Just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  const getStreamIcon = (type: Stream['type']) => {
    switch (type) {
      case 'public': return <PublicIcon fontSize="small" />;
      case 'private': return <PrivateIcon fontSize="small" />;
      case 'team': return <GroupIcon fontSize="small" />;
    }
  };

  const filteredPosts = selectedStream
    ? posts.filter(p => p.streamId === selectedStream.id)
    : posts;

  return (
    <Box sx={{ display: 'flex', height: '100%', bgcolor: '#f5f5f5' }}>
      {/* Sidebar - Streams List */}
      <Box
        sx={{
          width: 280,
          borderRight: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6" fontWeight={600}>Streams</Typography>
          <Typography variant="body2" color="text.secondary">
            Team collaboration and updates
          </Typography>
        </Box>

        {/* Search */}
        <Box sx={{ p: 1.5 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search streams..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Tabs */}
        <Tabs
          value={selectedTab}
          onChange={(_, v) => setSelectedTab(v)}
          sx={{ px: 1, minHeight: 36 }}
        >
          <Tab label="All" sx={{ minHeight: 36, py: 0 }} />
          <Tab label="My Streams" sx={{ minHeight: 36, py: 0 }} />
        </Tabs>

        {/* Streams List */}
        <Box sx={{ flexGrow: 1, overflow: 'auto', p: 1 }}>
          {/* All Streams option */}
          <Paper
            elevation={selectedStream === null ? 2 : 0}
            onClick={() => setSelectedStream(null)}
            sx={{
              p: 1.5,
              mb: 1,
              cursor: 'pointer',
              bgcolor: selectedStream === null ? 'primary.light' : 'transparent',
              '&:hover': { bgcolor: selectedStream === null ? 'primary.light' : 'action.hover' },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PublicIcon color={selectedStream === null ? 'primary' : 'action'} />
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body2" fontWeight={500}>All Streams</Typography>
                <Typography variant="caption" color="text.secondary">
                  View all activity
                </Typography>
              </Box>
            </Box>
          </Paper>

          {streams
            .filter(s => !searchQuery || s.name.toLowerCase().includes(searchQuery.toLowerCase()))
            .filter(s => selectedTab === 0 || s.isOwner)
            .map(stream => (
              <Paper
                key={stream.id}
                elevation={selectedStream?.id === stream.id ? 2 : 0}
                onClick={() => setSelectedStream(stream)}
                sx={{
                  p: 1.5,
                  mb: 1,
                  cursor: 'pointer',
                  bgcolor: selectedStream?.id === stream.id ? 'primary.light' : 'transparent',
                  '&:hover': { bgcolor: selectedStream?.id === stream.id ? 'primary.light' : 'action.hover' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {getStreamIcon(stream.type)}
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" fontWeight={500} noWrap>
                        {stream.name}
                      </Typography>
                      {stream.isMuted && <MuteIcon sx={{ fontSize: 14, color: 'text.secondary' }} />}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {stream.memberCount} members
                    </Typography>
                  </Box>
                  {stream.unreadCount > 0 && (
                    <Badge badgeContent={stream.unreadCount} color="primary" />
                  )}
                </Box>
              </Paper>
            ))}
        </Box>

        {/* Create Stream Button */}
        <Box sx={{ p: 1.5, borderTop: 1, borderColor: 'divider' }}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setShowCreateStream(true)}
          >
            Create Stream
          </Button>
        </Box>
      </Box>

      {/* Main Content - Posts */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', p: 3 }}>
        {/* Stream Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" fontWeight={600}>
            {selectedStream?.name || 'All Streams'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {selectedStream?.description || 'Activity from all your streams'}
          </Typography>
        </Box>

        {/* New Post Input */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main' }}>
                {(user?.firstName?.[0] || 'Y').toUpperCase()}
              </Avatar>
              <Box sx={{ flexGrow: 1 }}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  placeholder="Share something with your team..."
                  value={newPostContent}
                  onChange={(e) => setNewPostContent(e.target.value)}
                  variant="outlined"
                  sx={{ mb: 1 }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton size="small">
                    <ImageIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small">
                    <AttachIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small">
                    <EmojiIcon fontSize="small" />
                  </IconButton>
                  <Box sx={{ flexGrow: 1 }} />
                  <Button
                    variant="contained"
                    size="small"
                    endIcon={<SendIcon />}
                    onClick={handleCreatePost}
                    disabled={!newPostContent.trim() || !selectedStream}
                  >
                    Post
                  </Button>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Posts Feed */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : filteredPosts.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No posts yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Be the first to share something with your team!
            </Typography>
          </Box>
        ) : (
          filteredPosts.map(post => (
            <Card key={post.id} sx={{ mb: 2 }}>
              <CardContent>
                {/* Post Header */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                  <Avatar sx={{ mr: 2 }}>
                    {post.author.name[0].toUpperCase()}
                  </Avatar>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle2">{post.author.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatTimeAgo(post.timestamp)} • {post.streamName}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={(e) => setMoreMenuAnchor({ anchor: e.currentTarget, postId: post.id })}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Box>

                {/* Post Content */}
                <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                  {post.content}
                </Typography>

                {/* Post Actions */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton size="small" onClick={() => handleLikePost(post.id)}>
                      {post.likes.includes(currentUserId) ? (
                        <LikeIcon fontSize="small" color="primary" />
                      ) : (
                        <LikeOutlinedIcon fontSize="small" />
                      )}
                    </IconButton>
                    <Typography variant="caption" color="text.secondary">
                      {post.likes.length > 0 ? post.likes.length : ''}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton size="small">
                      <CommentIcon fontSize="small" />
                    </IconButton>
                    <Typography variant="caption" color="text.secondary">
                      {post.comments.length > 0 ? post.comments.length : ''}
                    </Typography>
                  </Box>
                  <IconButton size="small">
                    <ShareIcon fontSize="small" />
                  </IconButton>
                  <Box sx={{ flexGrow: 1 }} />
                  <IconButton size="small" onClick={() => handleBookmarkPost(post.id)}>
                    {post.isBookmarked ? (
                      <BookmarkIcon fontSize="small" color="primary" />
                    ) : (
                      <BookmarkOutlineIcon fontSize="small" />
                    )}
                  </IconButton>
                </Box>
              </CardContent>

              {/* Comments Section */}
              {post.comments.length > 0 && (
                <>
                  <Divider />
                  <Box sx={{ px: 2, py: 1, bgcolor: 'grey.50' }}>
                    {post.comments.map(comment => (
                      <Box key={comment.id} sx={{ display: 'flex', gap: 1.5, py: 1 }}>
                        <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>
                          {comment.author.name[0].toUpperCase()}
                        </Avatar>
                        <Box sx={{ flexGrow: 1 }}>
                          <Box sx={{ bgcolor: 'background.paper', p: 1.5, borderRadius: 1 }}>
                            <Typography variant="caption" fontWeight={500}>
                              {comment.author.name}
                            </Typography>
                            <Typography variant="body2">{comment.content}</Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            {formatTimeAgo(comment.timestamp)}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </>
              )}

              {/* Add Comment */}
              <Divider />
              <Box sx={{ p: 2, display: 'flex', gap: 1.5 }}>
                <Avatar sx={{ width: 32, height: 32 }}>
                  {(user?.firstName?.[0] || 'Y').toUpperCase()}
                </Avatar>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Write a comment..."
                  value={commentTexts[post.id] || ''}
                  onChange={(e) => setCommentTexts({ ...commentTexts, [post.id]: e.target.value })}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddComment(post.id)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => handleAddComment(post.id)}
                          disabled={!commentTexts[post.id]?.trim()}
                        >
                          <SendIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Card>
          ))
        )}
      </Box>

      {/* Post More Menu */}
      <Menu
        anchorEl={moreMenuAnchor?.anchor}
        open={Boolean(moreMenuAnchor)}
        onClose={() => setMoreMenuAnchor(null)}
      >
        <MenuItem onClick={() => setMoreMenuAnchor(null)}>
          <ListItemIcon><FlagIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Report</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => handleDeletePost(moreMenuAnchor?.postId || '')} sx={{ color: 'error.main' }}>
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Create Stream Dialog */}
      <Dialog open={showCreateStream} onClose={() => setShowCreateStream(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Stream</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Stream Name"
            value={newStreamName}
            onChange={(e) => setNewStreamName(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            fullWidth
            label="Description"
            value={newStreamDesc}
            onChange={(e) => setNewStreamDesc(e.target.value)}
            multiline
            rows={2}
            sx={{ mb: 2 }}
          />
          <Typography variant="subtitle2" gutterBottom>Stream Type</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip
              icon={<PublicIcon />}
              label="Public"
              onClick={() => setNewStreamType('public')}
              color={newStreamType === 'public' ? 'primary' : 'default'}
              variant={newStreamType === 'public' ? 'filled' : 'outlined'}
            />
            <Chip
              icon={<GroupIcon />}
              label="Team"
              onClick={() => setNewStreamType('team')}
              color={newStreamType === 'team' ? 'primary' : 'default'}
              variant={newStreamType === 'team' ? 'filled' : 'outlined'}
            />
            <Chip
              icon={<PrivateIcon />}
              label="Private"
              onClick={() => setNewStreamType('private')}
              color={newStreamType === 'private' ? 'primary' : 'default'}
              variant={newStreamType === 'private' ? 'filled' : 'outlined'}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreateStream(false)}>Cancel</Button>
          <Button onClick={handleCreateStream} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StreamsPage;
